'use client'

import { useEffect, useRef, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { ui, kawaii } from '../../lib/ui/theme'
import { useIsMobile } from '../../lib/ui/useIsMobile'
import { BAG_MOVES } from '../../lib/things/bag'

// Action → how it reads as a status + its accent colour. Keyed by the REAL
// server action (npc.action), so the label always matches what he's doing.
const ACTION_META = {
  IDLE:            { label: 'idle',        color: '#9ca3af' },
  MOVE:            { label: 'walking',     color: '#9ca3af' },
  FORAGE:          { label: 'eating',      color: '#f59e0b' },
  REST:            { label: 'resting',     color: '#34d399' },
  SLEEP:           { label: 'sleeping',    color: '#60a5fa' },
  INSPECT:         { label: 'looking',     color: '#a855f7' },
  SOCIALISE:       { label: 'socialising', color: '#ec4899' },
  WATCH:           { label: 'watching a film', color: '#f472b6' },
  GAMBLE:          { label: 'gambling',    color: '#ef4444' },
  BROWSE:          { label: 'browsing',    color: '#38bdf8' },
  RAVE:            { label: 'dancing',     color: '#a78bfa' },
  POST_TO_TWITTER: { label: 'posting',     color: '#38bdf8' },
  // Without these the feed printed the raw action name (survey, paddle,
  // scavenge...), which reads like debug output rather than a life.
  SURVEY:          { label: 'staring into the distance', color: '#7dd3fc' },
  PADDLE:          { label: 'paddling',    color: '#22d3ee' },
  SCAVENGE:        { label: 'scavenging',  color: '#a3a3a3' },
  TICKETS:         { label: 'looking for old tickets', color: '#efb46e' },
  DRINK:           { label: 'at the bar',  color: '#fb923c' },
  LAUNDRY:         { label: 'watching the machines', color: '#94a3b8' },
  MUSEUM:          { label: 'at the museum', color: '#c084fc' },
  PREEN:           { label: 'getting preened', color: '#f9a8d4' },
  TATTOO:          { label: 'watching the needle', color: '#f87171' },
  PHONE:           { label: 'on the phone', color: '#60a5fa' },
  STUDIO:          { label: 'making something', color: '#e8607a' },
  // the places he asked for at the shrine, all one verb
  GRANTED:         { label: 'somewhere he asked for', color: '#86efac' },
  // his bag's moves, worded where they are defined (lib/things/bag.js): the verb is the move's name in capitals
  ...Object.fromEntries(Object.entries(BAG_MOVES).map(([name, m]) => [name.toUpperCase(), { label: m.feed, color: '#d6a35c' }])),
}

const MAX_ITEMS = 6

// States the feed stays quiet about: the between-state and the two that
// are just him existing. The status line already reports these live.
const AMBIENT_ACTIONS = new Set(['IDLE', 'MOVE', 'SLEEP'])

const metaFor = (action) =>
  ACTION_META[action] || { label: String(action || '').toLowerCase().replace(/_/g, ' '), color: '#cbd5e1' }


// The bar a reason has to clear to be shown at all.
//
// These two surfaces, the caption under the bird and the bottom-right
// feed, are the most visible writing in the product, and they render the
// field that measured WORST of anything he produces: asking "why" scores
// -0.87 where asking what he noticed scores 0.01. Reasons left the diary
// this morning on that measurement and nobody moved them off the screen.
//
// The sim scores each reason as it is logged (EventLogger) so there is no
// second copy of the scorer here. Anything below the bar is simply not
// shown; the screen stays quiet instead of saying something bad. Every
// producer now goes through the scored logger (speak, tweets, dreams,
// browse reactions, verdicts, and the narrated MOMENT lines, which are
// the good register and used to have no path here at all), so an event
// with no score only exists in old buffers.
const MIN_REASON_SCORE = 0.5

// Where a thought came from, when a reader could not otherwise tell.
//
// The bare thought used to be the whole bubble, so a line he had while
// reading a web page arrived with nothing around it: "It reminded me that a
// shiny new coat doesn't magically fix the feeling of being a fledgling"
// reads as a riddle unless you already know he is sat in the internet cafe
// with a page open. Sam: "a visitor wouldn't immediately know he's talking
// about being on the internet here, and this is a huge initial point of
// onboarding".
//
// The event has carried the page title the whole time (Session.js sets it as
// the target). The diary card renders that as chrome and this surface never
// did, which is the whole bug. Only actions where the context is genuinely
// invisible are listed; a MOMENT already says where he is, in the line.
const SOURCE_LABEL = { BROWSE: 'reading', POST_TO_TWITTER: 'posted', JUDGE: 'an offering from' }
// A page with no title arrives as its address, and a forty-character
// address in a thought bubble is the least readable thing on the screen
// (measured 23 Sep: "reading https://www.binance.com/en/academy/articles/wh...").
// The site's name is what a person would say.
const siteOf = (raw) => {
  try {
    return new URL(raw).hostname.replace(/^www\./, '')
  } catch {
    return raw
  }
}
const sourceOf = (e) => {
  const text = SOURCE_LABEL[e?.action]
  if (!text) return null
  const raw = typeof e.target === 'string' ? e.target.trim() : ''
  const detail = e.action === 'JUDGE' && raw
    ? `@${raw.replace(/^@/, '')}`
    : /^https?:\/\//i.test(raw) ? siteOf(raw) : raw
  return { text, detail: detail.slice(0, 70) }
}

// A real, in-voice thought from an event's reason (not a placeholder).
const thoughtOf = (e) => {
  if (!e || e.action === 'THINK') return null
  const r = typeof e.reason === 'string' ? e.reason.trim() : ''
  if (r.length < 3) return null
  if (/^(it|wait|heading to it)\.?$/i.test(r)) return null
  if (typeof e.reasonScore === 'number' && e.reasonScore < MIN_REASON_SCORE) return null
  return r
}

function fmtTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

/**
 * Victor's feed — bottom-right. A running stream of what he's ACTUALLY doing
 * + what he's thinking, as chat-style messages, newest at the bottom.
 *
 * Two sources, deliberately kept separate so the action label is never a lie:
 *  • ACTION items come from npc.action (server-authoritative — the real action
 *    he's executing). The event log is the brain's *intent* and diverges from
 *    reality (it logs FORAGE while he's still MOVE-ing to the food; SLEEP is a
 *    sleep-cycle transition that logs no event at all), which is why the old
 *    feed said "eating" while he walked and "dancing" while he slept.
 *  • THOUGHT items come from the event reasons (his actual in-voice 'why').
 *
 * Lives in the uiVisible cluster (Tab hides). Replaces the old ActivityHUD +
 * bottom-centre caption + cognition bar.
 */
// Timestamp that sits in the bubble's bottom-right corner without ever
// pushing the text up a line.
//
// A plain float only works while the last line has room; when the text fills
// it, the float drops to a row of its own and you get the band of empty bubble
// we were trying to avoid. So instead: an inline spacer at the end of the text
// reserves exactly the stamp's width on the last line, and the stamp itself is
// positioned into the corner. Whatever the text does, the corner is already
// paid for.
const STAMP_W = 40

function StampSpacer() {
  return <span style={{ display: 'inline-block', width: STAMP_W, height: 1 }} aria-hidden="true" />
}

function Stamp({ ts }) {
  return (
    <span
      className="text-[9px]"
      style={{
        position: 'absolute',
        right: 12,
        bottom: 7,
        width: STAMP_W,
        textAlign: 'right',
        color: kawaii.inkSoft,
        fontFamily: ui.font,
        fontStyle: 'normal',
        fontWeight: 600,
      }}
    >
      {fmtTime(ts)}
    </span>
  )
}

/**
 * A thought cloud, in the same hand as the rest of the kit: flat fill, ink
 * outline, one soft highlight. Three overlapping lobes drawn as one path so
 * the stroke reads as a single edge rather than three circles touching.
 */
// The label above a bubble, saying what kind of thought this is. Dreams had
// one and nothing else did; it is the same markup either way, so it is one
// component now rather than two copies drifting apart.
function BubbleLabel({ icon = null, text, detail = '', tint }) {
  return (
    <div className="flex items-center gap-1.5" style={{ marginBottom: 5, minWidth: 0 }}>
      {icon}
      <span
        style={{
          background: tint,
          border: kawaii.outlineThin,
          borderRadius: 999,
          padding: '1px 8px',
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: '0.02em',
          color: kawaii.ink,
          fontFamily: ui.font,
          flexShrink: 0,
        }}
      >
        {text}
      </span>
      {detail && (
        <span
          className="truncate"
          style={{ fontSize: 10.5, color: kawaii.inkSoft, fontFamily: ui.font, minWidth: 0 }}
          title={detail}
        >
          {detail}
        </span>
      )}
    </div>
  )
}

function DreamCloud() {
  return (
    <svg width="17" height="17" viewBox="0 0 32 32" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      <path
        d="M9.5 23.5c-3.6 0-6-2.2-6-5.1 0-2.5 1.8-4.5 4.3-5 .2-3.9 3.4-6.9 7.5-6.9 3.4 0 6.3 2.1 7.2 5 .3-.05.7-.08 1-.08 3.1 0 5.5 2.3 5.5 5.2 0 3-2.4 5.3-5.6 5.3z"
        fill="#c9bff0"
        stroke={kawaii.ink}
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <ellipse cx="11.5" cy="14.5" rx="3" ry="2" fill="rgba(255,255,255,0.65)" />
      {/* the two trailing puffs that make it a thought rather than a cloud */}
      <circle cx="6.5" cy="26.5" r="2.1" fill="#c9bff0" stroke={kawaii.ink} strokeWidth="2" />
      <circle cx="2.8" cy="29.4" r="1.3" fill="#c9bff0" stroke={kawaii.ink} strokeWidth="1.8" />
    </svg>
  )
}

export function ChatFeed() {
  const mobile = useIsMobile()
  const events = useGameStore((s) => s.events)
  const action = useGameStore((s) => s.npcStates?.[0]?.action)
  // Quantised to 2% steps: the raw float moves every delta, and it was
  // re-rendering the whole bubble stack at 10Hz for a 40px progress bar.
  const progress = useGameStore((s) => {
    const p = s.npcStates?.[0]?.actionProgress
    return typeof p === 'number' ? Math.round(p * 50) / 50 : p
  })
  const consolidation = useGameStore((s) => s.memoryConsolidation)
  const endConsolidation = useGameStore((s) => s.endMemoryConsolidation)

  const [items, setItems] = useState([])
  const lastThoughtId = useRef(null)
  const lastAction = useRef(null)
  const seq = useRef(0)

  // THOUGHTS — append when a genuinely new in-voice reason arrives.
  useEffect(() => {
    let ev = null
    for (let i = events.length - 1; i >= 0; i--) {
      if (thoughtOf(events[i])) { ev = events[i]; break }
    }
    if (!ev || ev.id === lastThoughtId.current) return
    lastThoughtId.current = ev.id
    setItems((prev) => [
      ...prev,
      { kind: 'thought', id: `t-${ev.id}`, text: thoughtOf(ev), source: sourceOf(ev), ts: ev.timestamp || Date.now(), isDream: ev.action === 'DREAM' },
    ].slice(-MAX_ITEMS))
  }, [events])

  // ACTIONS — append on a real action transition.
  //
  // Skipped: IDLE (the between-state), and the two ambient ones. Walking
  // is 23% of his life and precedes every single activity, so MOVE alone
  // generated more bubbles than everything else combined, and SLEEP holds
  // for hours; a feed of "walking / sleeping / walking" is both boring and
  // already said better by the status line under the bird, which names the
  // live state in warm words and even knows he is heading home to bed.
  // What is left here is the stuff that happened: places, meals, films.
  useEffect(() => {
    if (!action || action === lastAction.current) return
    lastAction.current = action
    if (AMBIENT_ACTIONS.has(action)) return
    seq.current += 1
    setItems((prev) => [
      ...prev,
      { kind: 'action', id: `a-${seq.current}`, action, ts: Date.now() },
    ].slice(-MAX_ITEMS))
  }, [action])

  // Safety net: clear a stuck "consolidating" pill if the server's idle is missed.
  const consTimer = useRef(null)
  useEffect(() => {
    if (!consolidation?.active) return
    if (consTimer.current) clearTimeout(consTimer.current)
    consTimer.current = setTimeout(() => endConsolidation(), (consolidation.durationMs || 8000) + 2000)
    return () => { if (consTimer.current) clearTimeout(consTimer.current) }
  }, [consolidation?.active, consolidation?.durationMs, endConsolidation])

  if (!items.length && !consolidation?.active) return null

  // A phone gets three quieter bubbles above the status pill, not six full
  // width ones on top of it. The pill owns the floor; the feed floats clear.
  const shown = mobile ? items.slice(-3) : items

  // Index of the most recent action item — the live one gets a progress
  // line. Indexed against SHOWN, so on a phone the bar can never attach
  // to a bubble the slice just removed.
  let lastActionIdx = -1
  for (let i = shown.length - 1; i >= 0; i--) {
    if (shown[i].kind === 'action') { lastActionIdx = i; break }
  }
  // Only bar the live one. Now that walking and sleeping leave no bubble,
  // the newest action item can be something he has already finished (the
  // museum, while he is halfway home), and a progress bar under that would
  // be reporting one action's progress against another's name.
  const showProgress =
    progress > 0 && progress < 1 &&
    action && !AMBIENT_ACTIONS.has(action) &&
    lastActionIdx >= 0 && shown[lastActionIdx].action === action

  return (
    <>
      <div className="fixed z-50 pointer-events-none bottom-[76px] right-3 w-[min(64vw,300px)] md:bottom-6 md:right-6 md:w-[min(88vw,378px)] flex flex-col items-end gap-2">
        {shown.map((it, idx) => {
          const fromBottom = shown.length - 1 - idx
          const opacity = fromBottom === 0 ? 1 : Math.max(0.16, 1 - fromBottom * 0.24)

          if (it.kind === 'thought') {
            // A dream reads differently from a waking thought: a soft white
            // orb marks it, the bubble is dimmer and italic — sleep, not speech.
            const isDream = it.isDream
            return (
              <div key={it.id} className={isDream ? 'w-full' : 'max-w-full'} style={{ opacity, transition: 'opacity 450ms ease' }}>
                <div
                  className="relative px-3.5 py-2.5"
                  style={{
                    background: isDream ? '#e9e3f6' : kawaii.cream,
                    border: kawaii.outline,
                    borderRadius: 20,
                    boxShadow: kawaii.dropSm,
                  }}
                >
                  {/* The time floats at the END of the text's own block, so it
                      tucks onto the last line instead of claiming a row and
                      leaving a band of empty bubble under the words. */}
                  {isDream ? (
                    // Labelled rather than merely tinted. A dream and a
                    // waking thought were two shades of the same bubble, and
                    // the difference is the whole point: one is something he
                    // did, the other is something that happened to him while
                    // he was not there. So it gets a cloud, gets named, and
                    // the words sit under the label at the same left edge as
                    // every other line rather than indented into a column of
                    // their own.
                    <>
                      <BubbleLabel icon={<DreamCloud />} text="dream" tint="#c9bff0" />
                      <p className="text-[13px] leading-snug italic text-left" style={{ color: kawaii.ink, fontFamily: ui.font }}>
                        {it.text}
                        <StampSpacer />
                      </p>
                    </>
                  ) : (
                    <>
                      {it.source && (
                        <BubbleLabel text={it.source.text} detail={it.source.detail} tint={kawaii.creamDeep} />
                      )}
                      <p className="text-[13.5px] leading-snug text-left" style={{ color: kawaii.ink, fontFamily: ui.font, fontWeight: 500 }}>
                        {it.text}
                        <StampSpacer />
                      </p>
                    </>
                  )}
                  <Stamp ts={it.ts} />
                </div>
              </div>
            )
          }

          const meta = metaFor(it.action)
          const isLive = idx === lastActionIdx && it.action === action
          return (
            <div key={it.id} className="max-w-full" style={{ opacity, transition: 'opacity 450ms ease' }}>
              <div
                className="px-3.5 py-1.5 flex items-center gap-2"
                style={{ background: kawaii.cream, border: kawaii.outline, borderRadius: kawaii.radiusPill, boxShadow: kawaii.dropSm }}
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full shrink-0 ${isLive ? 'animate-pulse' : ''}`}
                  style={{ background: meta.color, border: `2px solid ${kawaii.ink}` }}
                />
                <span className="text-[12px]" style={{ color: kawaii.ink, fontFamily: ui.font, fontWeight: 700 }}>
                  {meta.label}
                </span>
                <span className="text-[9px] ml-1" style={{ color: kawaii.inkSoft, fontFamily: ui.font }}>{fmtTime(it.ts)}</span>
                {isLive && showProgress && (
                  <span className="ml-1 w-10 h-[3px] rounded-full overflow-hidden" style={{ background: kawaii.creamDeep }}>
                    <span className="block h-full" style={{ width: `${progress * 100}%`, background: meta.color, transition: 'width 120ms linear' }} />
                  </span>
                )}
              </div>
            </div>
          )
        })}

        {consolidation?.active && (
          <div className="max-w-full">
            <div
              className="px-3.5 py-1.5 flex items-center gap-2"
              style={{ background: '#e9e3f6', border: kawaii.outline, borderRadius: kawaii.radiusPill, boxShadow: kawaii.dropSm }}
            >
              <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: '#a855f7', border: `2px solid ${kawaii.ink}` }} />
              <span className="text-[12px]" style={{ color: kawaii.ink, fontFamily: ui.font, fontWeight: 700 }}>tucking the day away</span>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ChatFeed
